import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Discreet on-demand AI summary. Only shows the trigger button unless a
 * summary is loaded. Never auto-fires an LLM call.
 */
export default function SummarizeButton({ taskId, commentsCount }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/comments/summarize`);
      setSummary(data);
      setOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  if (commentsCount < 2) return null;

  return (
    <div data-testid="summarize-container" className="text-xs">
      {!summary ? (
        <button
          onClick={run}
          disabled={loading}
          data-testid="summarize-btn"
          className="inline-flex items-center gap-1 text-forest hover:underline"
        >
          <Sparkles className="w-3 h-3" />
          {loading ? "Sažimam…" : "Sažmi razgovor"}
        </button>
      ) : (
        <div className="border border-forest/25 bg-forest/6 rounded-md p-2.5">
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center gap-1 text-forest font-medium"
            data-testid="summarize-toggle"
          >
            <Sparkles className="w-3 h-3" />
            AI sažetak razgovora
            {open ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
          </button>
          {open && (
            <div className="mt-2 space-y-2 text-ink" data-testid="summary-body">
              {summary.odluceno && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-forest font-semibold">Odlučeno</div>
                  <div className="whitespace-pre-line">{summary.odluceno}</div>
                </div>
              )}
              {summary.otvoreno && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ochre-ink font-semibold">Otvoreno</div>
                  <div className="whitespace-pre-line">{summary.otvoreno}</div>
                </div>
              )}
              {summary.na_potezu && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-forest font-semibold">Na potezu</div>
                  <div className="whitespace-pre-line">{summary.na_potezu}</div>
                </div>
              )}
              {summary.sledeci_korak && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-forest font-semibold">Sledeći korak</div>
                  <div className="whitespace-pre-line">{summary.sledeci_korak}</div>
                </div>
              )}
              <div className="pt-1 flex justify-end">
                <button
                  onClick={() => setSummary(null)}
                  className="text-[10px] text-ink-muted hover:text-ink"
                >
                  Osveži
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
